import React from 'react';

import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';

import { IconButton, Typography, Box } from '@mui/material';

import { getRouteMediafileDetail, getRouteArtworkCreator } from 'routes';
import PlaylistAddCircleIcon from '@mui/icons-material/PlaylistAddCircle';
import BulkAddDialog from './BulkAddDialog'; // adjust path as needed

import ConfirmAddToPlaylist from './ConfirmAddToPlaylist';
import { fetchSelectedPlaylist, slidesToPlaylistInDomain } from 'restful';

import { ListOfUserPlaylist } from 'components';

// redux
import { connect } from 'react-redux';
import { openGlobalDialog } from 'redux/actions'; // actions
const useStyles = makeStyles((theme) => ({
	root: {
		padding: `${theme.spacing(0)} ${theme.spacing(1)}`,
		width: `100%`,
		color: 'inherit',
		fontSize: 'inherit',
		position: 'relative',
		display: 'flex',
	},
	actionIconButton: {
		padding: 4,
		border: `1px solid`,
		color: 'inherit',
		'&:hover': {
			backgroundColor: `rgba(0,0,0,0.1)`,
		},
	},
	filterMenuItem: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1),
		color: theme.palette.grey[500],
	},
}));

const weekArr = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const safeFormatDateTimeLocal = (dateString) => {
	if (!dateString) return '';

	const date = new Date(dateString);
	if (isNaN(date.getTime())) return '';

	const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
	return local.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm" for input type="datetime-local" in BulkAddDialog's TextField
};
function BulkAddToScreens({
	mediaFileValue,
	departments,
	userGroups,
	domainName,
	notifyGeneral,
	openGlobalDialog,
	variant = 'icon',
	menuText = 'Add to Screen Playlist',
	...rest
}) {
	const classes = useStyles();
	const intl = useIntl();

	const getInitialFormData = React.useCallback(() => {
		const startDate = safeFormatDateTimeLocal(mediaFileValue.postingDate);
		const endDate = safeFormatDateTimeLocal(mediaFileValue.expiryDate);

		return {
			selectedGroups: [],
			selectedDepartment: departments[0]?.id || '',
			playlistName: '',
			orientation: 'landscape',
			startDate,
			endDate,
			dailyStart: '',
			dailyEnd: '',
			selectedDays: weekArr,
			selectedDuration:
				mediaFileValue.fileType?.toUpperCase() === 'VIDEO' ? mediaFileValue.duration : 10,
		};
	}, [departments, mediaFileValue]);

	const [activeDialog, setActiveDialog] = React.useState(null); // 'bulk' | 'list' | 'confirm' | null
	const [formData, setFormData] = React.useState(getInitialFormData());
	const [bulkFilters, setBulkFilters] = React.useState('');
	const [confirmDialogData, setConfirmDialogData] = React.useState({});
	const [tempListOfUser, setTempListOfUser] = React.useState({
		isListOfPlaylist: false,
		isPlaylistPushed: false,
	});

	const handleFormFieldChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};
	const getFileType = (type) => {
		let newType = '';
		if (['video', 'mpeg-4', 'mpg', 'mp4'].indexOf(type.toLowerCase()) > -1) {
			newType = 'video';
			return newType;
		} else if (type.toLowerCase() === 'svg') {
			newType = 'svg';
			return newType;
		} else if (type.toLowerCase() === 'html') {
			newType = 'html';
			return newType;
		} else if (type.toLowerCase() === 'weburl') {
			newType = 'weburl';
			return newType;
		} else {
			newType = 'image';
			return newType;
		}
	};
	const handleNextButton = () => {
		if (formData.dailyEnd < formData.dailyStart) {
			notifyGeneral('End Time cannot be earlier than Start Time!', 'error');
			return;
		}
		const mediaOriginalUrl = mediaFileValue.originalUrl;
		const pathStartIndex = mediaOriginalUrl.indexOf(`${domainName}/`);
		const mediaPath = mediaOriginalUrl.substring(pathStartIndex);
		const filename = mediaPath.split('/').pop();
		const slides = [
			{
				schedule: {
					...(formData.startDate && {
						availableStart: new Date(formData.startDate).toISOString(),
					}),
					...(formData.endDate && {
						availableEnd: new Date(formData.endDate).toISOString(),
					}),
					...(formData.dailyStart && { visibleStartInADay: formData.dailyStart }),
					...(formData.dailyEnd && { visibleEndInADay: formData.dailyEnd }),
					dayOfWeek: formData.selectedDays.length > 0 ? formData.selectedDays : weekArr,
				},
				name: mediaFileValue.title,
				filename: filename,
				svgfilename:
					getFileType(mediaFileValue.fileType) === 'svg' && mediaFileValue.svgUrl
						? mediaFileValue.svgUrl.split('/').pop()
						: '',
				type: getFileType(mediaFileValue.fileType),
				duration: formData.selectedDuration,
				mediaId: mediaFileValue.id,
				mediaPath: mediaPath,
				mediaUrl:
					getFileType(mediaFileValue.fileType) === 'svg' && mediaFileValue.svgUrl
						? mediaFileValue.svgUrl
						: mediaFileValue.optimisedUrl,

				mediaPreviewUrl: mediaFileValue.previewUrl,
			},
		];
		const filters = {
			isMaster: false,
			playlistIds: [],
		};
		setBulkFilters({
			filters,
			slides,
		});
		setActiveDialog(null);
		setActiveDialog('list');
		setTempListOfUser((prev) => {
			return {
				...prev,
				isListOfPlaylist: true,
			};
		});
	};

	const handleContinueToAddPlaylist = async (selectedPlaylist) => {
		setFormData(getInitialFormData());
		setActiveDialog(null);
		setTempListOfUser((prev) => {
			return {
				...prev,
				isListOfPlaylist: false,
			};
		});
		const selectedIds = Object.entries(selectedPlaylist)
			.filter(([_, checked]) => checked)
			.map(([id]) => id);

		if (selectedIds.length === 0) {
			return;
		}
		try {
			const response = await fetchSelectedPlaylist({
				mediafileId: mediaFileValue.id,
				queryParams: { scope: 'domain' },
			});
			let playlistData = response.data.playlists;
			const playlistsHavingFile = playlistData?.map((p) => p.id) || [];
			const cleanedPlaylists = selectedIds.filter((id) => !playlistsHavingFile.includes(id));
			const alreadyExistingCount = selectedIds.length - cleanedPlaylists.length;
			if (cleanedPlaylists.length === 0) {
				notifyGeneral(
					'This file has already been added to all of the playlists you selected.',
					'error'
				);
			} else {
				const extraText = intl.formatMessage(
					{
						id: 'pages.MediaFiles.components.BulkAddToScreens.ConfirmDialogMessage3',
					},
					{ count: alreadyExistingCount }
				);
				setConfirmDialogData({
					previewUrl: mediaFileValue.previewUrl,
					count: cleanedPlaylists.length,
					extra: alreadyExistingCount > 0 ? extraText : '',
				});
				setBulkFilters((prev) => ({
					...prev,
					filters: {
						...prev.filters,
						playlistIds: cleanedPlaylists,
					},
				}));
				setActiveDialog('confirm');
			}
		} catch (error) {
			console.error('Scope check failed. Proceeding without it:', error);
			setConfirmDialogData({
				previewUrl: mediaFileValue.previewUrl,
				count: selectedIds.length,
				extra: '',
			});
			setBulkFilters((prev) => ({
				...prev,
				filters: {
					...prev.filters,
					playlistIds: selectedIds,
				},
			}));
			setActiveDialog('confirm');
		}
	};
	const handleConfirmAddToPlaylist = async () => {
		setActiveDialog(null);
		try {
			await slidesToPlaylistInDomain({
				bodyParams: bulkFilters,
			});
			setActiveDialog('list');
			setTempListOfUser((prev) => {
				return {
					...prev,
					isListOfPlaylist: true,
					isPlaylistPushed: true,
				};
			});
		} catch (error) {
			console.error('Error adding slides to playlists:', error);
			notifyGeneral('Error adding slides to playlists:', 'error');
		}
	};

	const handleBulkAddToScreen = () => {
		if (mediaFileValue.isArtworkTemplate) {
			let confirmDialog = {
				size: 'sm',
				title: intl.formatMessage({
					id: 'pages.MediaFiles.components.MediafileList.ScreensMenuListDialogTitle',
				}),
				content: intl.formatMessage({
					id: 'pages.MediaFiles.components.MediafileList.ScreensMenuListDialogContent',
				}),
				confirmCB: () => {
					if (mediaFileValue.isEsign) {
						window.location.assign(getRouteMediafileDetail(mediaFileValue.id));
					} else {
						window.location.assign(getRouteArtworkCreator(mediaFileValue.id));
					}
				},
			};
			openGlobalDialog(confirmDialog);
		} else {
			setActiveDialog('bulk');
		}
	};
	const renderBulkAddToScreensTrigger = () => {
		if (variant === 'menuItem') {
			return (
				<Box
					onClick={() => handleBulkAddToScreen()}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						width: '100%',
						cursor: 'pointer',
					}}
				>
					{/* Icon with Text */}
					<IconButton
						aria-label="AddToManyScreens"
						className={classes.actionIconButton}
						onClick={() => handleBulkAddToScreen()}
						title="Add To Many Screens"
						size="small"
					>
						<PlaylistAddCircleIcon fontSize="small" />
					</IconButton>
					<Typography
						variant="body2"
						sx={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							flex: 1,
						}}
					>
						{menuText}
					</Typography>
				</Box>
			);
		}

		// Default icon button
		return (
			<IconButton
				aria-label="AddToManyScreens"
				className={classes.actionIconButton}
				onClick={() => handleBulkAddToScreen()}
				title="Add To Many Screens"
				size="small"
			>
				<PlaylistAddCircleIcon fontSize="small" />
			</IconButton>
		);
	};
	return (
		<div>
			{renderBulkAddToScreensTrigger()}

			{/* Add File to Multiple Playlists dialog */}
			{activeDialog === 'bulk' && (
				<BulkAddDialog
					open={true}
					onClose={() => setActiveDialog(null)}
					formData={formData}
					classes={classes}
					userGroups={userGroups}
					departments={departments}
					mediaFileValue={mediaFileValue}
					handleFormFieldChange={handleFormFieldChange}
					handleNextButton={handleNextButton}
					intl={intl}
				/>
			)}
			{/* List of user dialog */}
			<ListOfUserPlaylist
				key={`${activeDialog}-${tempListOfUser.isPlaylistPushed}`}
				mediaFileValue={mediaFileValue}
				dialogState={{
					open: tempListOfUser.isListOfPlaylist,
					readOnly: tempListOfUser.isPlaylistPushed,
				}}
				// isOpen={true}
				onClose={() => {
					setFormData(getInitialFormData());
					setTempListOfUser((prev) => ({
						...prev,
						isListOfPlaylist: false,
						isPlaylistPushed: false,
					}));
					setActiveDialog(null);
				}}
				onHandleContinue={handleContinueToAddPlaylist}
				playlistFilters={{
					selectedDepartment: formData.selectedDepartment,
					selectedGroups: formData.selectedGroups,
					orientation: formData.orientation,
					playlistName: formData.playlistName,
				}}
				classes={classes}
			/>
			{/* Confirm dialog */}
			{activeDialog === 'confirm' && (
				<ConfirmAddToPlaylist
					isOpen={true}
					onClose={() => {
						setFormData(getInitialFormData());
						setTempListOfUser((prev) => ({
							...prev,
							isListOfPlaylist: false,
						}));
						setActiveDialog(null);
					}}
					onConfirmAddtoPlaylist={handleConfirmAddToPlaylist}
					confirmDialogData={confirmDialogData}
					intl={intl}
				/>
			)}
		</div>
	);
}

BulkAddToScreens.propTypes = {
	mediaFileValue: PropTypes.object,
	departments: PropTypes.array.isRequired,
	userGroups: PropTypes.array.isRequired,
	domainName: PropTypes.string.isRequired,
	notifyGeneral: PropTypes.func.isRequired,
	openGlobalDialog: PropTypes.func.isRequired,
	variant: PropTypes.string,
	menuText: PropTypes.string,
};

BulkAddToScreens.defaultProps = {};

const mapStateToProps = () => {
	return {};
};

export default connect(mapStateToProps, { openGlobalDialog })(BulkAddToScreens);
